import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import DraggableModal from 'components/organisms/draggableModal';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import EmergencyModal from 'components/molecules/rpm-alert-assessment/emergencyModal';
import FormItemWrapper from 'components/molecules/rpm-alert-assessment/formItemWrapper';
import { ASSESSMENT_PREFIX } from 'infrastructure/consts/assessment-modal';

import { assessmentPulseForm } from '../templates/pulse';
import s from '../styles.module.scss';
import { criticalPulseValidationSchema } from '../validation-schema';
import { usePulseAssessment } from '../hooks/use-pulse-assessment';
import { checkSymptomsUpdate, scrollToErrorElement } from '../helpers';
import { nursingInterventionOptions } from '../templates/common';

import type { ICriticalPulseProps } from '../types';
import type { FC } from 'react';

const CriticalPulseModal: FC<ICriticalPulseProps> = ({
  isOpen,
  activityId,
  type,
  activityDate,
  values,
  patientGuid,
  idx,
  handleSubmit,
  closeModal,
}) => {
  const {
    doctor: { addComment },
  } = useDispatch();

  const { t } = useTranslation();

  const {
    initialValues,
    openEmergencyModal,
    symptomsList,
    toggleEmergencyModal,
    onSaveForm,
  } = usePulseAssessment({
    type,
    activityId,
    activityDate,
    addComment,
    values,
    patientGuid,
    closeModal,
    handleSubmit,
  });

  const blockRef = useRef<HTMLDivElement | null>(null);
  const formWrapperRef = useRef<HTMLDivElement>(null);
  const localStorageFormKey = `${ASSESSMENT_PREFIX}-${idx}_${type}-pulse`;
  const storedData = localStorage.getItem(localStorageFormKey);
  const parsedData: typeof initialValues | null = storedData
    ? JSON.parse(storedData)
    : null;

  const formik = useFormik({
    initialValues: parsedData ?? initialValues,
    onSubmit: (data) => {
      if (data.isOneMoreReading === 'false') {
        data.bounds = null;
        data.repeatReadingWas = null;
      }
      if (data.patient !== null) {
        data.other = null;
        data.relationship = null;
      }
      const isEscalation = data.nursingIntervention?.includes(
        nursingInterventionOptions[1].label,
      );
      if (!isEscalation) {
        data.escalationToClinic = [];
      }

      localStorage.setItem(localStorageFormKey, JSON.stringify(data));

      onSaveForm(data);
    },
    validateOnChange: false,
    validationSchema: criticalPulseValidationSchema(symptomsList),
  });

  const requiredSymptomFields = symptomsList
    .filter((symptom) => symptom.required)
    .map((symptom) => symptom.value);

  const subTitleModal =
    type === 'max'
      ? t('labels.tachycardiaAssessment')
      : t('labels.bradycardiaAssessment');

  const onSave = async () => {
    await formik.submitForm();
    scrollToErrorElement(formWrapperRef.current);
  };

  const onEmergencyModalCancel = () => {
    formik.setFieldValue('emergencyCare', false);
    toggleEmergencyModal();
  };

  const onEmergencyModalSave = () => {
    formik.setFieldValue('emergencyCare', true);
    toggleEmergencyModal();
    if (blockRef.current) {
      blockRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (formik.values.checkSymptoms) {
      checkSymptomsUpdate(requiredSymptomFields, formik, toggleEmergencyModal);
    }
  }, [formik.values]);

  const expandableExtraFields = ['isOneMoreReading'];

  return (
    <>
      <DraggableModal
        isOpen={isOpen}
        toggle={closeModal}
        title={t('labels.rpmAlertBasedAssessment', {
          name: subTitleModal,
        })}
        onSave={onSave}
      >
        <BaseForm formik={formik}>
          <div className={s.wrapper} ref={formWrapperRef}>
            {assessmentPulseForm.map((formItem, index) => {
              const isShowExtraFields = expandableExtraFields.includes(
                formItem.name,
              )
                ? formik.getFieldMeta(formItem.name)?.value === 'true'
                : undefined;

              return (
                <FormItemWrapper
                  formik={formik}
                  index={index}
                  formItem={formItem}
                  type={type}
                  key={formItem.key}
                  requiredSymptomFields={requiredSymptomFields}
                  ref={blockRef}
                  isShowExtraFields={isShowExtraFields}
                />
              );
            })}
          </div>
        </BaseForm>
      </DraggableModal>

      {openEmergencyModal && (
        <EmergencyModal
          openEmergencyModal={openEmergencyModal}
          onSave={onEmergencyModalSave}
          onCancel={onEmergencyModalCancel}
        />
      )}
    </>
  );
};

export default CriticalPulseModal;
